import { get, getBlob, post, put } from '@/shared/utils/FetchV2.util';

export type EngineCycleState =
	| 'mes_en_curso'
	| 'preparando'
	| 'necesita_algo'
	| 'listo_para_aprobar'
	| 'en_revision'
	| 'presentando'
	| 'presentada'
	| 'por_pagar'
	| 'pagada'
	| 'sin_presentar';

export type EngineResult = 'a_pagar' | 'saldo_a_favor' | 'en_ceros';

export type EngineRegime = '626' | '625' | '612';

export type EnginePayrollEvent = 'aguinaldo' | 'ptu' | 'prima_vacacional' | 'separacion' | 'jubilacion' | 'nuevo_patron';

export type EnginePayrollSummary = {
	receipts: number;
	net: number;
	gross: number;
	withheld: number;
	employers: string[];
	events?: EnginePayrollEvent[];
};

export type EnginePayrollLine = { code: string; label: string; amount: number; exempt?: number };

export type EnginePayrollReceipt = {
	id: string;
	uuid: string;
	position?: string;
	department?: string;
	dailySalary?: number;
	employedSince?: string;
	employerRegistration?: string;
	ssnLast4?: string;
	employer: string;
	employerRfc: string;
	paidOn: string;
	periodStart?: string;
	periodEnd?: string;
	daysPaid?: number;
	frequency?: string;
	extraordinary: boolean;
	net: number;
	gross: number;
	withheld: number;
	earnings: EnginePayrollLine[];
	deductions: EnginePayrollLine[];
	otherPayments: EnginePayrollLine[];
};

export type EnginePayrollMonth = {
	period: string;
	net: number;
	gross: number;
	deductions: number;
	otherPayments: number;
	withheld: number;
	exempt: number;
	receipts: EnginePayrollReceipt[];
	yearGross: number;
	yearWithheld: number;
};

export type EngineYearMonth = {
	period: string;
	state: EngineCycleState | null;
	result: EngineResult | null;
	total: number | null;
	income?: number;
	regime?: EngineRegime;
	regimes: EngineRegime[];
	cells?: {
		regime: EngineRegime;
		state: EngineCycleState;
		result: EngineResult | null;
		total: number | null;
		income?: number;
		receiptChecked?: boolean;
	}[];
	payroll?: EnginePayrollSummary;
	pastDue?: boolean;
	receiptChecked?: boolean;
};

export type EngineAnnualState = 'en_curso' | 'presentada' | 'por_pagar' | 'pendiente' | 'sin_obligacion' | 'sin_datos';

export type EngineInstallmentStatus = 'pagada' | 'siguiente' | 'pendiente' | 'vencida';

export type EngineAnnualInstallment = {
	number: number;
	amount: number;
	dueOn: string;
	status: EngineInstallmentStatus;
	paidOn?: string;
	captureLine?: string;
	document?: string;
};

export type EngineAnnualSummary = {
	year: number;
	income: number;
	state: EngineAnnualState;
	reasons?: string[];
	deadline: string;
	presentedOn?: string;
	result?: EngineResult;
	total?: number;
	paid?: boolean;
	installments?: EngineAnnualInstallment[];
	salaryIncome?: number;
	refundStatus?: { state: string; kind: string; amount?: number };
	purchased?: boolean;
	/** Pendiente de un ejercicio cerrado y sin contratar: se puede vender. */
	sellable?: boolean;
	/** Días desde el vencimiento, solo si está pendiente en un ejercicio cerrado. */
	daysLate?: number;
};

export type EngineAnnualLine = { code: string; label: string; amount: number; sign: '+' | '-' | '=' };

export type EngineAnnualRefund = {
	case: 'automatica' | 'fed' | 'compensacion' | 'sin_seleccion' | 'esperando_sat';
	kind?: string;
	state?: string;
	folio?: string;
	requested?: number;
	resolved?: number;
	paid?: number;
	paidOn?: string;
	clabeLast4?: string;
	satText?: string;
	updatedAt?: string;
	terminal: boolean;
	fed?: { folio?: string; presentedOn: string; requested?: number; office?: string; attachments: string[] };
	history: { observedAt: string; state: string }[];
	others: { kind: string; folio?: string; state: string; updatedAt: string }[];
	timeline?: EngineRefundMilestone[];
	view?: EngineRefundView;
};

export type EngineRefundCategory =
	| 'compensacion'
	| 'pagada'
	| 'requerimiento'
	| 'terminada'
	| 'en_proceso'
	| 'automatica_en_revision'
	| 'preparando';

export type EngineRefundChoice =
	| 'pedir_deposito'
	| 'pedir_resto'
	| 'quedarme_con_lo_depositado'
	| 'compensar'
	| 'seguir_esperando'
	| 'presentar_de_nuevo'
	| 'responder_requerimiento'
	| 'me_interesa';

export type EngineRefundExpense = { issuedOn: string; issuer: string; concept: string; paymentForm?: string; amount: number };

export type EngineRefundView = {
	category: EngineRefundCategory;
	subcategory?: string;
	held: boolean;
	inFavor: number;
	paid?: number;
	paidOn?: string;
	pending?: number;
	partialPayment: boolean;
	outsideWindow: boolean;
	annualPresentedOn?: string;
	automatic?: { state: string; folio?: string; presentedOn?: string; resolvedOn?: string };
	fed?: { state: string; controlNumber?: string; presentedOn?: string; businessDays: number; requested?: number; hasAcuse: boolean };
	comparison: { concept: string; sat: number; declared: number }[];
	platform?: { income: number; deductions: number; profit: number };
	expenses?: { total: number; categories: { name: string; total: number; items: EngineRefundExpense[] }[] };
	requirement?: { notifiedOn?: string; deadline?: string; estimated?: boolean; asks?: string[]; document?: string };
	documents: { efirma?: boolean; bankStatement: boolean };
	choices: EngineRefundChoice[];
	choice?: { choice: EngineRefundChoice; on: string; bankStatement: boolean };
	computedAt: string;
};

export type EngineRefundFinding = {
	reason?: string;
	ask?: string;
	compare: { concept: string; sat: number; declared: number }[];
};

export type EngineRefundMilestone = {
	kind: string;
	event: 'pedida' | 'resultado' | 'solicitud';
	state?: string;
	on?: string;
	amount?: number;
	folio?: string;
	finding?: EngineRefundFinding;
};

export type EnginePersonalDeductionItem = {
	uuid?: string;
	rubro: string;
	rubroSat?: string;
	verdict: 'aceptada' | 'no_clasificada' | 'observada';
	reasons: string[];
	observations: string[];
	amount: number;
	issuedOn?: string;
	issuerName?: string;
};

export type EnginePersonalDeductions = {
	inVisor: boolean;
	seen: number;
	accepted: number;
	unclassified: number;
	observed: number;
	amount: number;
	acceptedAmount: number;
	satUpdatedOn?: string;
	items: EnginePersonalDeductionItem[];
	notInVisor: { uuid: string; issuerName?: string; amount: number; issuedOn: string; use: string }[];
};

export type EnginePayrollVisor = {
	income: number;
	exempt: number;
	withheld: number;
	subsidy: number;
	employers: number;
	satUpdatedOn?: string;
	byEmployer: { rfc: string; name: string; from?: string; to?: string; income: number; exempt: number; withheld: number }[];
};

export type EngineAnnualDetail = EngineAnnualSummary & {
	paymentUnconfirmed: boolean;
	filing?: { operationNumber: string; type: string; presentedOn: string; byHeru: boolean; filings: number };
	determination: EngineAnnualLine[];
	chapters: { code: string; name: string; lines: EngineAnnualLine[] }[];
	balance?: { option: 'devolucion' | 'compensacion' | 'sin_seleccion'; bank?: string; clabeLast4?: string };
	captureLine?: { line: string; validUntil: string; expired: boolean; amount?: number };
	payments: { amount: number; paidOn: string; bank?: string; method?: string }[];
	documents: Partial<Record<'acuse' | 'detalle' | 'pago' | 'fed', string>>;
	refund?: EngineAnnualRefund;
	personalDeductions?: EnginePersonalDeductions;
	payrollVisor?: EnginePayrollVisor;
};

export type EngineYear = {
	year: number;
	kind?: 'sueldos';
	obligatedMonths?: number;
	months: EngineYearMonth[];
	annual?: EngineAnnualSummary;
};

export type EngineCatchUpPeriod = {
	period: string;
	regime: EngineRegime;
	state: EngineCycleState;
	result: EngineResult | null;
	total: number | null;
	surcharges: number;
	income?: number;
	hadRegime?: boolean;
	/** Días desde el vencimiento del pago. */
	daysLate: number;
	/** sin_presentar y ninguna compra de Heru lo cubre. */
	sellable: boolean;
};

export type EngineSyncState = 'sin_perfil' | 'reconectar' | 'sincronizando' | 'listo';

export type EnginePresentationMode = 'automatico' | 'manual';

export type EnginePresentation = { mode: EnginePresentationMode | null };

export type EngineOverview = {
	rfc: string;
	presentation?: EnginePresentation;
	linked: boolean;
	sync: EngineSyncState;
	/** Como sync, más el candado de frescura: solo con listo se puede cotizar catchUp o afirmar que no debe nada. */
	catchUpSync: EngineSyncState;
	catchUpSyncReason?: string;
	today: string;
	focusPeriod: string;
	years: EngineYear[];
	catchUp: EngineCatchUpPeriod[];
	unfiledCount: number;
	unconfirmedCount?: number;
};

export type EngineTaxLine = {
	tax: 'ISR' | 'IVA';
	rate?: number;
	result: number;
	operations: { label: string; amount: number; sign: '+' | '-' }[];
};

export type EngineConcept = {
	id?: string;
	productCode?: string;
	productName?: string;
	description: string;
	quantity?: number;
	unit?: string;
	unitPrice?: number;
	amount: number;
	iva: number;
	deductible?: boolean;
	reasonCode?: string;
	source?: string;
	overridable: boolean;
	pending: boolean;
};

export type EngineCreditNote = {
	side: 'gasto' | 'ingreso';
	adjusts?: string;
	adjustsOn?: string;
	effect?: 'gasto' | 'iva' | 'ingreso' | 'ninguno' | 'pendiente';
	reason?: 'no_deducible' | 'definitiva' | 'sin_destino' | 'anticipo' | 'omitida';
	deductible?: number;
	iva?: number;
};

export type EngineMovement = {
	id: string;
	kind: 'ingreso' | 'deducible' | 'no_deducible';
	docType: 'Factura' | 'Factura global' | 'Constancia de retenciones' | 'Nota de crédito';
	name: string;
	counterpartyRfc?: string;
	concept: string;
	date: string;
	amount: number;
	subtotal: number;
	iva: number;
	isrWithheld: number;
	ivaWithheld: number;
	uuid?: string;
	paymentForm?: string;
	paymentMethod?: string;
	currency?: string;
	exchangeRate?: number;
	installment: boolean;
	issuedByHeru?: boolean;
	creditNote?: EngineCreditNote;
	notCounted?: boolean;
	concepts: EngineConcept[];
};

export type EngineDocumentKind = 'acuse' | 'detalle' | 'pago';

export type EnginePeriodDetail = {
	period: string;
	regime: EngineRegime;
	regimeName: string;
	regimes: EngineRegime[];
	lane: 'ronda' | 'reguz';
	state: EngineCycleState;
	result: EngineResult | null;
	total: number | null;
	receiptChecked?: boolean;
	forecast?: { total: number; asOf: string };
	paymentDeadline: string;
	request?: { ifNoAnswer: 'se_presenta' | 'se_detiene'; filesOn?: string };
	money: {
		taxLines: EngineTaxLine[];
		movements: EngineMovement[];
		definitive: boolean;
	} | null;
	signedBy?: { who: 'tu' | 'heru'; at: string };
	submissionStep?: 'login' | 'plantilla' | 'modelo' | 'firmado' | 'recibe' | 'acuse';
	presentedOn?: string;
	operationNumber?: string;
	documents?: Partial<Record<EngineDocumentKind, string>>;
	captureLine?: {
		line: string;
		validUntil: string;
		expired: boolean;
		amount?: number;
		renewed?: boolean;
		originalAmount?: number;
	};
	payment?: {
		paidOn?: string;
		amount: number;
		bank?: string;
		paymentKey?: string;
		captureLine?: string;
	};
	priorPayment?: EnginePeriodDetail['payment'];
	block?: { since: string; heldPeriods: number };
	locked?:
		| { kind: 'compra'; promo?: 'first_free' | 'discount_50' }
		| { kind: 'renovacion'; renewsOn: string; subscriptionId?: number }
		| { kind: 'renovacion_vencida'; renewsOn?: string; subscriptionId?: number }
		| { kind: 'renovando' };
	purchase?: { productVersionId: number; future: boolean };
	review?: EngineReview;
	newLine?: { status: 'generando' | 'fallo'; requestedAt: string };
	paymentCheck?: { reportedAt: string };
	surcharges?: number;
	recalculating: boolean;
	askCash: boolean;
	needs: string[];
	notice?: { code: string; at: string };
	sourceCheck?: EngineSourceCheck;
	approvalHeld?: EngineApprovalHeld;
	voided?: boolean;
	history?: EngineActivityItem[];
	capabilities: EngineCapabilities;
};

export type EngineMissingSource = {
	key: string;
	name: string;
	kind: 'plataforma' | 'propia' | 'nomina' | 'interes';
	waiting: boolean;
};

export type EngineSourceCheck = { sources: EngineMissingSource[] };

export type EngineApprovalHeld = { approvedAt: string; reason: 'lectura' | 'calculo' | 'constancias' | 'revision' };

export type EngineReviewReason = 'falta_gasto' | 'sobra_ingreso' | 'otra' | 'revision_heru';

export type EngineReview = {
	reason: EngineReviewReason;
	comment?: string;
	reportedAt: string;
	answer?: { message?: string; note?: string; at: string };
};

export type EngineCapabilities = {
	approve: boolean;
	object: boolean;
	reportCash: boolean;
	changeDeductions: boolean;
	reportPayment: boolean;
	newCaptureLine: boolean;
	captureLinesLeft: number;
};

export type EngineActionReceipt = {
	status: 'recibida' | 'recalculando' | 'pedida';
	requestedAt: string;
};

const BASE = '/declarations/new/me';

export const getDeclarationsNewOverview = async (): Promise<EngineOverview> =>
	get({ service: 'fiscalEngine', endpoint: `${BASE}/overview` });

export const getDeclarationsNewPeriod = async (period: string, regime?: EngineRegime): Promise<EnginePeriodDetail> =>
	get({
		service: 'fiscalEngine',
		endpoint: `${BASE}/periods/${period}${regime ? `?regime=${regime}` : ''}`,
	});

export const getDeclarationsNewPayroll = async (period: string): Promise<EnginePayrollMonth> =>
	get({ service: 'fiscalEngine', endpoint: `${BASE}/payroll/${period}` });

export const getDeclarationsNewAnnual = async (year: number): Promise<EngineAnnualDetail> =>
	get({ service: 'fiscalEngine', endpoint: `${BASE}/annuals/${year}` });

export const getDeclarationsNewDocument = async (path: string): Promise<Blob> => getBlob({ service: 'fiscalEngine', endpoint: `/${path}` });

export const chooseDeclarationsNewRefund = (year: number, body: { choice: EngineRefundChoice; bankStatementUuid?: string }) =>
	post<{ received: boolean }>({
		service: 'fiscalEngine',
		endpoint: `${BASE}/annuals/${year}/refund/choice`,
		body,
		skipAccountError: true,
	});

export type BankStatementUpload = { file_url: string; file_uuid: string; bank_name: string };

export const uploadDeclarationsNewBankStatement = ({ file, rfc, period, bankName }: { file: File; rfc: string; period: string; bankName: string }) => {
	const body = new FormData();
	body.append('rfc', rfc);
	body.append('period', period);
	body.append('bank_name', bankName);
	body.append('file', file);
	return post<BankStatementUpload>({ service: 'fiscalEngine', endpoint: '/bank-statement/web/upload', body, skipAccountError: true });
};

export const setDeclarationsNewPresentation = (body: { mode: EnginePresentationMode; textVersion: string }): Promise<EngineActionReceipt> =>
	put({
		service: 'fiscalEngine',
		endpoint: `${BASE}/presentation`,
		body,
		skipAccountError: true,
	});

const periodAction = (period: string, action: string, body: object): Promise<EngineActionReceipt> =>
	post({
		service: 'fiscalEngine',
		endpoint: `${BASE}/periods/${period}/${action}`,
		body,
		skipAccountError: true,
	});

export const approveDeclarationsNewPeriod = (
	period: string,
	body: { regime: EngineRegime; totalSeen: number; acceptedSources?: string[] },
) =>
	periodAction(period, 'approval', body);

export const objectDeclarationsNewPeriod = (
	period: string,
	body: {
		regime: EngineRegime;
		reason: Exclude<EngineReviewReason, 'revision_heru'>;
		comment?: string;
	},
) => periodAction(period, 'objection', body);

export const reportDeclarationsNewCash = (period: string, body: { regime: '625'; received: boolean; amount?: number }) =>
	periodAction(period, 'cash', body);

export const reportDeclarationsNewPayment = (period: string, body: { regime: EngineRegime }) =>
	periodAction(period, 'payment-report', body);

export const requestDeclarationsNewCaptureLine = (period: string, body: { regime: EngineRegime }) =>
	periodAction(period, 'capture-line', body);

export const markDeclarationsNewDeduction = (
	period: string,
	conceptId: string,
	body: { regime: EngineRegime; deductible: boolean },
): Promise<EngineActionReceipt> =>
	put({
		service: 'fiscalEngine',
		endpoint: `${BASE}/periods/${period}/concepts/${encodeURIComponent(conceptId)}/deduction`,
		body,
		skipAccountError: true,
	});

export const engineErrorMessage = (error: unknown): string | undefined => {
	const data = (error as { response?: { data?: { detail?: string; message?: string } } })?.response?.data;
	return data?.detail ?? data?.message;
};

export const engineErrorStatus = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status;

export type EngineActivityKind =
	| 'factura'
	| 'declaracion_presentada'
	| 'complementaria_presentada'
	| 'linea_nueva'
	| 'declaracion_sin_efecto'
	| 'anual_presentada'
	| 'pago_registrado'
	| 'lista_para_aprobar'
	| 'accion_usuario'
	| 'accion_no_completada'
	| 'efectivo_reportado'
	| 'revision_respondida';

export type EngineActivityItem = {
	id: string;
	kind: EngineActivityKind;
	action?: 'aprobar' | 'objetar' | 'pago_reportado' | 'nueva_linea';
	at: string;
	period?: string;
	regime?: EngineRegime;
	year?: number;
	title?: string;
	detail?: string;
	amount?: number;
	result?: EngineResult;
	movement?: EngineMovement;
	classified: boolean;
};

export type EngineActivity = { items: EngineActivityItem[]; next?: string };

export const getDeclarationsNewActivity = async ({
	limit,
	year,
	before,
}: {
	limit: number;
	year: number;
	before?: string;
}): Promise<EngineActivity> =>
	get({
		service: 'fiscalEngine',
		endpoint: `${BASE}/activity?limit=${limit}&year=${year}${before ? `&before=${encodeURIComponent(before)}` : ''}`,
	});
